import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import DownloadQuotesDialog from 'pages/InsuranceQuoteForm/components/DownloadQuotesDialog';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useGetBrokerLeadsQuery } from 'services/api/brokerDashboard.api';
import { useGetQuoteStatusQuery, useGetQuotesQuery } from 'services/api/insuranceQuoteForm.api';
import type { BrokerLeadSummary } from 'services/types/brokerDashboard';
import type { InsuranceQuoteListItem } from 'services/types/insuranceQuoteForm';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SURFACE,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';

import QuoteProgressColumn from './QuoteProgressColumn';

const TEAL = '#25616F';
const SURFACE_HOVER = '#eceaff';

const POLLING_INTERVAL = 10000;

const PAYMENT_METHOD_OPTIONS = ['UPFRONT', 'PAC', 'PAT', 'INSTALLMENTS'];
const PAYMENT_FREQUENCY_OPTIONS = ['MONTHLY', 'ANNUAL', 'TOTAL'];

const getInsuranceLogoPath = (company: string) => {
  const logoName = company
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  return `/logos/insurance/${logoName}.png`;
};

const quoteColumns: GridColDef[] = [
  {
    field: 'insuranceCompany',
    headerName: 'Aseguradora',
    flex: 1,
    minWidth: 130,
    renderCell: (params) => {
      const value = params.value as string;
      if (!value) return '-';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Box
            component="img"
            src={getInsuranceLogoPath(value)}
            alt={value}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.textContent = value;
            }}
            sx={{ height: 40, width: 'auto', objectFit: 'contain' }}
          />
        </Box>
      );
    },
  },
  { field: 'planName', headerName: 'Plan', flex: 1, minWidth: 140 },
  {
    field: 'deductible',
    headerName: 'Deducible',
    flex: 0.6,
    minWidth: 90,
    type: 'number',
    valueFormatter: (value: number) => (value != null ? String(value) : '-'),
  },
  {
    field: 'premiumUF',
    headerName: 'Prima (UF)',
    flex: 0.7,
    minWidth: 100,
    type: 'number',
    valueFormatter: (value: number | null) => (value != null ? value.toLocaleString('es-CL') : '-'),
  },
  {
    field: 'premiumCLP',
    headerName: 'Prima (CLP)',
    flex: 0.7,
    minWidth: 110,
    type: 'number',
    valueFormatter: (value: number | null) =>
      value != null ? value.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' }) : '-',
  },
  {
    field: 'paymentMethod',
    headerName: 'Forma de pago',
    flex: 0.8,
    minWidth: 110,
    type: 'singleSelect',
    valueOptions: PAYMENT_METHOD_OPTIONS,
    valueFormatter: (value: string) =>
      ({ UPFRONT: 'Contado', PAC: 'PAC', PAT: 'PAT', INSTALLMENTS: 'Cuotas' })[value] ?? value ?? '-',
  },
  {
    field: 'paymentFrequency',
    headerName: 'Frecuencia',
    flex: 0.7,
    minWidth: 100,
    type: 'singleSelect',
    valueOptions: PAYMENT_FREQUENCY_OPTIONS,
    valueFormatter: (value: string) => ({ MONTHLY: 'Mensual', ANNUAL: 'Anual', TOTAL: 'Total' })[value] ?? value ?? '-',
  },
];

interface BrokerLeadsViewProps {
  brokerBusinessUnitId?: number;
  initialLeadId?: number | null;
  onClearLeadParam?: () => void;
}

function formatClientName(lead: BrokerLeadSummary): string {
  const cd = lead.clientData;
  if (!cd) return 'Sin datos';
  const name = [cd.name, cd.firstLastname, cd.secondLastname].filter(Boolean).join(' ');
  return name || cd.rut || 'Sin datos';
}

function formatRut(rut: string): string {
  const clean = rut.replace(/[^0-9kK]/g, '');
  if (clean.length < 2) return rut;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1).toUpperCase();
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formatted}-${dv}`;
}

function formatVehicle(lead: BrokerLeadSummary): string {
  const vd = lead.vehicleData;
  if (!vd) return '';
  const parts = [vd.licensePlate, vd.brand, vd.model, vd.year].filter(Boolean);
  return parts.join(' - ');
}

interface LeadQuotesPanelProps {
  lead: BrokerLeadSummary;
}

const LeadQuotesPanel: React.FC<LeadQuotesPanelProps> = ({ lead }) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);

  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: 25 });
    setFilterModel({ items: [] });
    setSortModel([]);
  }, [lead.leadId]);

  const { data: quotesData } = useGetQuotesQuery({
    leadId: lead.leadId,
    paginationModel: JSON.stringify(paginationModel),
    filterModel: JSON.stringify(filterModel),
    sortModel: JSON.stringify(sortModel),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <PersonOutlineIcon sx={{ fontSize: 20, color: NAVY, opacity: 0.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: NAVY }}>
            {formatClientName(lead)}
            {lead.clientData?.rut && (
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                ({formatRut(lead.clientData.rut)})
              </Typography>
            )}
          </Typography>
        </Box>
        {formatVehicle(lead) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <DirectionsCarOutlinedIcon sx={{ fontSize: 18, color: NAVY, opacity: 0.4 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {formatVehicle(lead)}
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <CustomDataGrid<InsuranceQuoteListItem>
          rows={quotesData?.rows ?? []}
          columns={quoteColumns}
          filterModel={filterModel}
          sortModel={sortModel}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={quotesData?.count ?? 0}
          getRowClassName={() => ''}
          pageId="broker-lead-quotes"
          noResultsMessage="No hay cotizaciones para este lead."
          topBar={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                overflow: 'scroll',
              }}
            >
              <Button
                sx={{
                  textTransform: 'none',
                  borderRadius: RADIUS_COIN,
                  color: NAVY,
                  '&:hover': { backgroundColor: SURFACE_CONTAINER_LOW },
                }}
                onClick={() => setOpenDownloadDialog(true)}
                disabled={(quotesData?.count ?? 0) === 0}
              >
                Descargar Cotizaciones
              </Button>
            </Box>
          }
        />
      </Box>
      <DownloadQuotesDialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
        leadId={lead.leadId}
        filterModel={JSON.stringify(filterModel)}
        sortModel={JSON.stringify(sortModel)}
        totalQuotes={quotesData?.count ?? 0}
      />
    </Box>
  );
};

const BrokerLeadsView: React.FC<BrokerLeadsViewProps> = ({ brokerBusinessUnitId, initialLeadId, onClearLeadParam }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const initialLeadHandled = useRef(false);

  useEffect(() => {
    if (initialLeadId && !initialLeadHandled.current) {
      initialLeadHandled.current = true;
      setSelectedLeadId(initialLeadId);
      onClearLeadParam?.();
    }
  }, [initialLeadId, onClearLeadParam]);

  const {
    data,
    isLoading,
    refetch: refetchLeads,
  } = useGetBrokerLeadsQuery({
    brokerBusinessUnitId,
    paginationModel: JSON.stringify({ page, pageSize }),
  });

  const rows = data?.rows || [];
  const totalCount = data?.count || 0;
  const selectedLead = rows.find((l) => l.leadId === selectedLeadId) || null;

  const { data: statusData, refetch: refetchStatus } = useGetQuoteStatusQuery(selectedLeadId ?? 0, {
    skip: !selectedLeadId,
  });

  const pendingCompanies = statusData?.companies.filter((c) => c.status === 'pending' || c.status === 'running') ?? [];
  const isLeadInProgress = pendingCompanies.length > 0;

  useEffect(() => {
    if (!selectedLeadId || !isLeadInProgress) return undefined;
    const intervalId = setInterval(() => {
      refetchStatus().catch(() => {});
    }, POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [selectedLeadId, isLeadInProgress, refetchStatus]);

  const prevTotalQuotes = useRef(statusData?.totalQuotes);
  useEffect(() => {
    if (statusData && prevTotalQuotes.current !== undefined && statusData.totalQuotes !== prevTotalQuotes.current) {
      refetchLeads().catch(() => {});
    }
    prevTotalQuotes.current = statusData?.totalQuotes;
  }, [statusData?.totalQuotes, refetchLeads, statusData]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        gap: 0,
        borderRadius: RADIUS_COIN,
        overflow: 'hidden',
        boxShadow: SHADOW,
      }}
    >
      {/* Col 1: Lead list */}
      <Box
        sx={{
          width: 360,
          minWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: SURFACE_CONTAINER_LOW,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            px: 2,
            py: 1.5,
            fontWeight: 600,
            color: NAVY,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.75rem',
          }}
        >
          Leads
        </Typography>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {isLoading && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: NAVY }}>
                Cargando...
              </Typography>
            </Box>
          )}
          {!isLoading && rows.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No se encontraron leads
              </Typography>
            </Box>
          )}
          <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 1 }}>
            {rows.map((lead) => (
              <ListItemButton
                key={lead.leadId}
                selected={lead.leadId === selectedLeadId}
                onClick={() => setSelectedLeadId(lead.leadId)}
                sx={{
                  alignItems: 'flex-start',
                  py: 1.5,
                  borderRadius: RADIUS_CARD,
                  transition: 'all 0.2s ease',
                  '&.Mui-selected': {
                    backgroundColor: SURFACE_CONTAINER_LOWEST,
                    boxShadow: SHADOW,
                    '&:hover': { backgroundColor: SURFACE_CONTAINER_LOWEST },
                  },
                  '&:hover': { backgroundColor: SURFACE_HOVER },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: NAVY }}>
                        {formatClientName(lead)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1, flexShrink: 0 }}>
                        {new Date(lead.createdAt).toLocaleDateString('es-CL')}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      {lead.clientData?.rut && (
                        <Typography variant="caption" component="span" sx={{ color: 'text.secondary' }}>
                          {lead.clientData.rut}
                        </Typography>
                      )}
                      {formatVehicle(lead) && (
                        <Typography variant="caption" component="span" sx={{ color: 'text.secondary' }}>
                          {formatVehicle(lead)}
                        </Typography>
                      )}
                      <Box component="span" sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        <Chip
                          label={`${lead.totalQuotes} cotiz.`}
                          size="small"
                          sx={{
                            backgroundColor: NAVY,
                            color: SURFACE_CONTAINER_LOWEST,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                        <Chip
                          label={`${lead.companies.length} comp.`}
                          size="small"
                          sx={{
                            backgroundColor: TEAL,
                            color: SURFACE_CONTAINER_LOWEST,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                    </Box>
                  }
                  slotProps={{
                    secondary: { component: 'div' },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por pág:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ '.MuiTablePagination-toolbar': { minHeight: 48 } }}
        />
      </Box>
      {/* Col 2: Progress column */}
      {selectedLeadId && isLeadInProgress && (
        <Box
          sx={{
            width: 200,
            minWidth: 200,
            backgroundColor: SURFACE,
            transition: 'width 0.3s ease, opacity 0.3s ease',
          }}
        >
          <QuoteProgressColumn companies={pendingCompanies} />
        </Box>
      )}
      {/* Col 3: Quotes detail */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          backgroundColor: SURFACE_CONTAINER_LOWEST,
        }}
      >
        {!selectedLead && !selectedLeadId && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: 'text.secondary' }}>Selecciona un lead para ver sus cotizaciones</Typography>
          </Box>
        )}
        {selectedLead && <LeadQuotesPanel lead={selectedLead} />}
        {selectedLeadId && !selectedLead && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: 'text.secondary' }}>Cargando datos del lead...</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BrokerLeadsView;
